import {useQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/BillingProvidersService';
import {BillingProvider} from '../types.ts';
import {billingProvidersQueryKeys} from './billingProvidersQueryKeys';

// FNP-616: the channel selector is now CATALOG-DRIVEN. The legal set of billing channels is
// the set of ENABLED BillingProvider rows (their lowercase-underscore `key` is the canonical
// channel token — one vocabulary shared with the metering channel + provider_mappings).
// Replaces the hardcoded BillingChannel enum that used to enumerate STRIPE/AWS_MARKETPLACE/
// INTERNAL on the frontend: onboarding a provider no longer needs a UI change.
//
// Returns FormikSelect-shaped options ({value, label}) where value = the catalog key (the
// token persisted on MeterAssignment.channel) and label = the human name (falls back to key).
//
// ⚠️ The billing-api resolver ignores filter.status (FNP-1895): the response carries
// Disabled providers too. The status check below is what keeps them out of the pickers;
// the filter is still sent so the request is right once the resolver honours it.

export interface ChannelOption {
    value: string;
    label: string;
}

export const useChannelOptions = () => {
    const query = useQuery({
        queryKey: billingProvidersQueryKeys.channelOptions(),
        queryFn: () => fetchRecords({filter: {status: 'Enabled'}, limit: 100}),
        staleTime: 30_000,
    });

    const options: ChannelOption[] = (query.data?.items ?? [])
        .filter((p): p is BillingProvider => !!p && !!p.key && p.status === 'Enabled')
        .map((p) => ({value: p.key, label: p.name ?? p.key}));

    return {
        options,
        loading: query.isLoading,
        error: query.error,
    };
};
